import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminCatalogProvider, useAdminCatalog } from './contexts/AdminCatalogContext';
import { AdminUnsavedProvider } from './contexts/AdminUnsavedContext';
import { AdminDialogProvider } from './contexts/AdminDialogContext';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import NoticiasPage from './pages/NoticiasPage';
const ExperienciasPage = lazy(() => import('./pages/ExperienciasPage'));
import ExperienciasHomeClienteLayoutPage from './pages/ExperienciasHomeClienteLayoutPage';
import TarifariosPage from './pages/TarifariosPage';
import GuiasPage from './pages/GuiasPage';
import AssociacaoPage from './pages/AssociacaoPage';
import AssociacaoFormPage from './pages/AssociacaoFormPage';
import ClientesPage from './pages/ClientesPage';
import ExcursoesPage from './pages/ExcursoesPage';
import NovaExcursaoPage from './pages/NovaExcursaoPage';
import RankingPage from './pages/RankingPage';
import CidadesPage from './pages/CidadesPage';
import DestinosExportPage from './pages/DestinosExportPage';
import ExperienciasExportPage from './pages/ExperienciasExportPage';
import NoticiasExportPage from './pages/NoticiasExportPage';
import GuiaExportPage from './pages/GuiaExportPage';
import AgenciasExportPage from './pages/AgenciasExportPage';
import GuiasListExportPage from './pages/GuiasListExportPage';
import GuiaConvitesStorePage from './pages/GuiaConvitesStorePage';
import GuiaSyncReceiverPage from './pages/GuiaSyncReceiverPage';
import FinanceiroPixExportPage from './pages/FinanceiroPixExportPage';
import EstabelecimentosPage from './pages/EstabelecimentosPage';
import HospedagensPage from './pages/HospedagensPage';
import EventosPage from './pages/EventosPage';
import CuponsPage from './pages/CuponsPage';
import FinanceiroPage from './pages/FinanceiroPage';
import PerfilPage from './pages/PerfilPage';
import IcalAutoSyncProvider from './contexts/IcalAutoSyncContext';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { catalogReady, catalogLoading, catalogError, reloadCatalog } = useAdminCatalog();
  if (!user) return <Navigate to="/login" replace />;
  if (catalogError && !catalogReady) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-gray-50">
        <p className="text-red-700 text-center max-w-md text-sm">{catalogError}</p>
        <p className="text-gray-600 text-center max-w-md text-xs">
          Verifique a API (<code className="bg-white px-1 rounded border">VITE_API_URL</code> ou{' '}
          <code className="bg-white px-1 rounded border">VITE_ADMIN_SYNC_URL</code>) e as tabelas DynamoDB.
        </p>
        <button
          type="button"
          onClick={() => void reloadCatalog()}
          className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900"
        >
          Tentar novamente
        </button>
      </div>
    );
  }
  if (!catalogReady) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600 text-sm bg-gray-50">
        A carregar catálogo da API…
      </div>
    );
  }
  return <>{children}</>;
}

function App() {
  const routerBasename =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')
      ? '/admin'
      : '/';

  return (
    <AuthProvider>
      <AdminCatalogProvider>
      <IcalAutoSyncProvider>
        <Router basename={routerBasename}>
          <AdminDialogProvider>
            <AdminUnsavedProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
            <Route path="/destinos-export" element={<DestinosExportPage />} />
            <Route path="/experiencias-export" element={<ExperienciasExportPage />} />
            <Route path="/noticias-export" element={<NoticiasExportPage />} />
            <Route path="/guia-export" element={<GuiaExportPage />} />
            <Route path="/agencias-export" element={<AgenciasExportPage />} />
            <Route path="/guias-list-export" element={<GuiasListExportPage />} />
            <Route path="/guia-convites-store" element={<GuiaConvitesStorePage />} />
            <Route path="/guia-sync-receiver" element={<GuiaSyncReceiverPage />} />
            <Route path="/financeiro-pix-export" element={<FinanceiroPixExportPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/noticias"
              element={
                <ProtectedRoute>
                  <NoticiasPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/experiencias/home-cliente"
              element={
                <ProtectedRoute>
                  <ExperienciasHomeClienteLayoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/experiencias"
              element={
                <ProtectedRoute>
                  <Suspense
                    fallback={
                      <div className="min-h-[50vh] flex items-center justify-center text-gray-600 text-sm">
                        A carregar experiências…
                      </div>
                    }
                  >
                    <ExperienciasPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/estabelecimentos"
              element={
                <ProtectedRoute>
                  <EstabelecimentosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hospedagens"
              element={
                <ProtectedRoute>
                  <HospedagensPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eventos"
              element={
                <ProtectedRoute>
                  <EventosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cupons"
              element={
                <ProtectedRoute>
                  <CuponsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tarifarios"
              element={
                <ProtectedRoute>
                  <TarifariosPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/guias"
              element={
                <ProtectedRoute>
                  <GuiasPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/associacao/nova"
              element={
                <ProtectedRoute>
                  <AssociacaoFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/associacao/:associacaoId/editar"
              element={
                <ProtectedRoute>
                  <AssociacaoFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/associacao"
              element={
                <ProtectedRoute>
                  <AssociacaoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clientes"
              element={
                <ProtectedRoute>
                  <ClientesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/excursoes"
              element={
                <ProtectedRoute>
                  <ExcursoesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cidades"
              element={
                <ProtectedRoute>
                  <CidadesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ranking"
              element={
                <ProtectedRoute>
                  <RankingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/financeiro"
              element={
                <ProtectedRoute>
                  <FinanceiroPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/perfil"
              element={
                <ProtectedRoute>
                  <PerfilPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/excursoes/nova"
              element={
                <ProtectedRoute>
                  <NovaExcursaoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/excursoes/:id/editar"
              element={
                <ProtectedRoute>
                  <NovaExcursaoPage />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
            </AdminUnsavedProvider>
          </AdminDialogProvider>
        </Router>
      </IcalAutoSyncProvider>
      </AdminCatalogProvider>
    </AuthProvider>
  );
}

export default App;
